import { TextInput } from "@mantine/core";
import IconSearch from "@src/assets/icons/tabler/search.svg?react";
import IconX from "@src/assets/icons/tabler/x.svg?react";
import { useFlatNavbarTree } from "@src/components/board/queries/fetch-ranklist";
import type { Whiteboard } from "@src/components/board/schema";
import { NavbarSection } from "@src/gql/graphql";
import Fuse from "fuse.js";
import { Suspense, useEffect } from "react";
import { useSearch } from "./context/search-context";

const MIN_NAV_ITEMS_FOR_SEARCH = 10;

export function useShowNavBarSearch() {
	const myWorkspaceItems = useFlatNavbarTree(NavbarSection.MyWhiteboards);
	const sharedWithMeItems = useFlatNavbarTree(NavbarSection.SharedWithMe);
	return (
		myWorkspaceItems.length + sharedWithMeItems.length >=
		MIN_NAV_ITEMS_FOR_SEARCH
	);
}

export function getFuseInstance(whiteboards: Whiteboard[]) {
	return new Fuse(whiteboards, {
		keys: ["name"],
		threshold: 0.3,
	});
}

export function filterByQuery(
	whiteboards: Whiteboard[],
	fuse: Fuse<Whiteboard>,
	query?: string,
) {
	if (!query?.trim()) {
		return whiteboards;
	}

	return fuse.search(query).map((item) => item.item);
}

export function NavBarSearchComponent({ className }: { className?: string }) {
	return (
		<Suspense fallback={null}>
			<NavBarSearch className={className} />
		</Suspense>
	);
}

type NavBarSearchUiProps = {
	searchQuery?: string;
	disabled?: boolean;
	className?: string;
	handleChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	handleClear?: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

export function NavBarSearchUI(props: NavBarSearchUiProps) {
	const {
		searchQuery = "",
		disabled = false,
		className,
		handleChange,
		handleClear,
	} = props;

	return (
		<div className={className}>
			<TextInput
				classNames={{
					input:
						"bg-transparent hover:bg-surface-hover-subtle focus:bg-transparent border-border-subtle focus:border-brand-primary h-8 min-h-8 text-xs transition-colors",
				}}
				value={searchQuery}
				disabled={disabled}
				placeholder="Search boards, folders, cards..."
				leftSection={<IconSearch className="w-3.5 h-3.5 text-text-secondary" />}
				rightSection={
					searchQuery && (
						<button
							type="button"
							aria-label="Clear search"
							title="Clear search"
							className="flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-interaction-ghost-hover rounded-md cursor-pointer pointer-events-auto"
							onClick={handleClear}
						>
							<IconX className="w-3.5 h-3.5" />
						</button>
					)
				}
				onChange={handleChange}
				size="xs"
				radius="md"
			/>
		</div>
	);
}

export function NavBarSearch({ className }: { className?: string }) {
	const { searchQuery, setSearchQuery } = useSearch();
	const showSearch = useShowNavBarSearch();

	useEffect(() => {
		if (!showSearch && searchQuery) {
			setSearchQuery("");
		}
	}, [showSearch, searchQuery, setSearchQuery]);

	if (!showSearch) {
		return null;
	}

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(e.currentTarget.value);
	};

	const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
		setSearchQuery("");
		e.currentTarget.blur();
	};

	return (
		<NavBarSearchUI
			className={className}
			searchQuery={searchQuery}
			handleChange={handleChange}
			handleClear={handleClear}
		/>
	);
}
